import React, { useState, useEffect } from 'react';
import { Search, Plus, DollarSign, User, Calendar, Check, Clock, Trash2, Edit2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface TeacherPayment {
    id: string;
    created_at: string;
    teacher_name: string;
    lesson_type: 'private' | 'course' | 'exam';
    lesson_subject?: string;
    payment_name?: string;
    hours_worked: number;
    hourly_rate: number;
    total_amount: number;
    currency: string;
    payment_date?: string;
    payment_day?: string;
    status: 'pending' | 'paid';
    notes?: string;
    payment_source?: string;
}

interface MonthlyStats {
    totalPending: number;
    paidByCurrency: Record<SupportedCurrency, number>;
    teacherCount: number;
    hoursWorked: number;
}

type SupportedCurrency = 'TRY' | 'EUR' | 'USD';

const SUPPORTED_CURRENCIES: SupportedCurrency[] = ['TRY', 'EUR', 'USD'];

const TeacherPayments: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [payments, setPayments] = useState<TeacherPayment[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid'>('all');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingPayment, setEditingPayment] = useState<TeacherPayment | null>(null);
    const [selectedPaidCurrency, setSelectedPaidCurrency] = useState<SupportedCurrency>('TRY');
    const [stats, setStats] = useState<MonthlyStats>({
        totalPending: 0,
        paidByCurrency: { TRY: 0, EUR: 0, USD: 0 },
        teacherCount: 0,
        hoursWorked: 0
    });

    // Form state
    const [formData, setFormData] = useState({
        teacherName: '',
        lessonType: 'private' as 'private' | 'course' | 'exam',
        lessonSubject: '',
        paymentName: '',
        hoursWorked: '',
        hourlyRate: '',
        currency: 'EUR',
        paymentDate: '',
        paymentDay: '',
        status: 'pending' as 'pending' | 'paid',
        paymentSource: '',
        notes: ''
    });

    const WEEKDAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const getWeekdayName = (dateString: string) => {
        if (!dateString) return '';
        const parts = dateString.split('-');
        if (parts.length !== 3) return '';
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        if (isNaN(date.getTime())) return '';
        return WEEKDAYS[date.getDay()];
    };

    const handleDateChange = (dateVal: string) => {
        const dayVal = getWeekdayName(dateVal);
        setFormData(prev => ({
            ...prev,
            paymentDate: dateVal,
            paymentDay: dayVal || prev.paymentDay
        }));
    };

    useEffect(() => {
        fetchPayments();
    }, []);

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('testprep_teacher_payments')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setPayments(data || []);
            calculateStats(data || []);
        } catch (error) {
            console.error('Error fetching teacher payments:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (data: TeacherPayment[]) => {
        const pending = data.filter(p => p.status === 'pending').reduce((sum, p) => sum + Number(p.total_amount), 0);
        const paidByCurrency = data
            .filter(p => p.status === 'paid')
            .reduce<Record<SupportedCurrency, number>>((totals, payment) => {
                const currency = payment.currency?.toUpperCase() as SupportedCurrency;

                if (SUPPORTED_CURRENCIES.includes(currency)) {
                    totals[currency] += Number(payment.total_amount);
                }

                return totals;
            }, { TRY: 0, EUR: 0, USD: 0 });
        const teachers = new Set(data.map(p => p.teacher_name)).size;
        const hours = data.reduce((sum, p) => sum + Number(p.hours_worked), 0);

        setStats({ totalPending: pending, paidByCurrency, teacherCount: teachers, hoursWorked: hours });
    };

    const handleSave = async () => {
        try {
            const total = Number(formData.hoursWorked) * Number(formData.hourlyRate);

            const payload = {
                teacher_name: formData.teacherName,
                lesson_type: formData.lessonType,
                lesson_subject: formData.lessonSubject || null,
                payment_name: formData.paymentName || null,
                hours_worked: Number(formData.hoursWorked),
                hourly_rate: Number(formData.hourlyRate),
                total_amount: total,
                currency: formData.currency,
                payment_date: formData.status === 'paid' ? (formData.paymentDate || new Date().toISOString().split('T')[0]) : null,
                payment_day: formData.status === 'paid' ? (formData.paymentDay || getWeekdayName(formData.paymentDate || new Date().toISOString().split('T')[0])) : null,
                status: formData.status,
                payment_source: formData.status === 'paid' ? (formData.paymentSource || null) : null,
                notes: formData.notes || null
            };

            if (editingPayment) {
                const { error } = await supabase
                    .from('testprep_teacher_payments')
                    .update(payload)
                    .eq('id', editingPayment.id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('testprep_teacher_payments')
                    .insert(payload);
                if (error) throw error;
            }

            setShowAddModal(false);
            setEditingPayment(null);
            resetForm();
            fetchPayments();
        } catch (error) {
            console.error('Error saving payment:', error);
            alert('Kayıt sırasında hata oluştu.');
        }
    };

    const handleMarkAsPaid = async (id: string) => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const weekday = getWeekdayName(today);
            const { error } = await supabase
                .from('testprep_teacher_payments')
                .update({ 
                    status: 'paid', 
                    payment_date: today,
                    payment_day: weekday
                })
                .eq('id', id);

            if (error) throw error;
            fetchPayments();
        } catch (error) {
            console.error('Error updating payment:', error);
        }
    };

    const handleEditClick = (payment: TeacherPayment) => {
        setEditingPayment(payment);
        setFormData({
            teacherName: payment.teacher_name,
            lessonType: payment.lesson_type,
            lessonSubject: payment.lesson_subject || '',
            paymentName: payment.payment_name || '',
            hoursWorked: String(payment.hours_worked),
            hourlyRate: String(payment.hourly_rate),
            currency: payment.currency,
            paymentDate: payment.payment_date || '',
            paymentDay: payment.payment_day || '',
            status: payment.status,
            paymentSource: payment.payment_source || '',
            notes: payment.notes || ''
        });
        setShowAddModal(true);
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu ödeme kaydını silmek istediğinizden emin misiniz?')) return;

        try {
            const { error } = await supabase
                .from('testprep_teacher_payments')
                .delete()
                .eq('id', id);

            if (error) throw error;
            fetchPayments();
        } catch (error) {
            console.error('Error deleting payment:', error);
        }
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setEditingPayment(null);
        resetForm();
    };

    const resetForm = () => {
        setFormData({
            teacherName: '',
            lessonType: 'private',
            lessonSubject: '',
            paymentName: '',
            hoursWorked: '',
            hourlyRate: '',
            currency: 'EUR',
            paymentDate: '',
            paymentDay: '',
            status: 'pending',
            paymentSource: '',
            notes: ''
        });
    };

    const filteredPayments = payments.filter(p => {
        const matchesSearch = p.teacher_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.payment_name && p.payment_name.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getLessonTypeLabel = (type: string) => {
        switch (type) {
            case 'private': return 'Özel Ders';
            case 'course': return 'Kurs';
            case 'exam': return 'Deneme Sınavı';
            default: return type;
        }
    };

    const getCurrencySymbol = (currency: string) => {
        switch (currency) {
            case 'EUR': return '€';
            case 'USD': return '$';
            case 'TRY': return '₺';
            default: return currency;
        }
    };

    const formatCurrency = (amount: number, currency: SupportedCurrency) => new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Hoca Ödemeleri</h2>
                    <p className="text-gray-600 dark:text-gray-400">Öğretmen ücret takibi ve ödeme yönetimi</p>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>Yeni Ödeme Ekle</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Bekleyen Ödemeler</p>
                            <p className="text-2xl font-bold text-red-600">€{stats.totalPending.toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                            <Clock className="w-6 h-6 text-red-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1 pr-3">
                            <div className="mb-1 flex items-center gap-2">
                                <p className="text-sm text-gray-500">Ödenen Toplam</p>
                                <select
                                    value={selectedPaidCurrency}
                                    onChange={(event) => setSelectedPaidCurrency(event.target.value as SupportedCurrency)}
                                    aria-label="Ödenen toplam para birimi"
                                    className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs font-semibold text-gray-700 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                >
                                    <option value="TRY">TL</option>
                                    <option value="EUR">EUR</option>
                                    <option value="USD">USD</option>
                                </select>
                            </div>
                            <p className="truncate text-2xl font-bold text-green-600">
                                {formatCurrency(stats.paidByCurrency[selectedPaidCurrency], selectedPaidCurrency)}
                            </p>
                        </div>
                        <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                            <Check className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Aktif Hocalar</p>
                            <p className="text-2xl font-bold text-blue-600">{stats.teacherCount}</p>
                        </div>
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                            <User className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Toplam Saat</p>
                            <p className="text-2xl font-bold text-purple-600">{stats.hoursWorked}</p>
                        </div>
                        <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                            <Calendar className="w-6 h-6 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Hoca veya öğrenci ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setStatusFilter('all')}
                        className={`px-4 py-2 text-sm font-medium ${statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                    >
                        Tümü
                    </button>
                    <button
                        onClick={() => setStatusFilter('pending')}
                        className={`px-4 py-2 text-sm font-medium ${statusFilter === 'pending' ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                    >
                        Bekleyen
                    </button>
                    <button
                        onClick={() => setStatusFilter('paid')}
                        className={`px-4 py-2 text-sm font-medium ${statusFilter === 'paid' ? 'bg-green-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                    >
                        Ödendi
                    </button>
                </div>
            </div>

            {/* Payments Table */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
                ) : filteredPayments.length > 0 ? (
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hoca</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ders Tipi</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ödeme Adı</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saat</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ücret</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Toplam</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kaynağı</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredPayments.map((payment) => (
                                <tr key={payment.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${payment.status === 'pending' ? 'bg-red-50/30 dark:bg-red-900/5' : ''}`}>
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-gray-900 dark:text-white">{payment.teacher_name}</div>
                                        <div className="text-xs text-gray-500">{payment.lesson_subject}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                                            {getLessonTypeLabel(payment.lesson_type)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 font-medium">
                                        {payment.payment_name || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                                        {payment.hours_worked}s
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                        {getCurrencySymbol(payment.currency)}{payment.hourly_rate}/s
                                    </td>
                                    <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-white">
                                        {getCurrencySymbol(payment.currency)}{payment.total_amount}
                                    </td>
                                    <td className="px-6 py-4 text-xs text-gray-600 dark:text-gray-400 font-medium">
                                        {payment.payment_source || '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${payment.status === 'paid'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                            {payment.status === 'paid' ? 'Ödendi' : 'Bekliyor'}
                                        </span>
                                        {payment.status === 'paid' && payment.payment_date && (
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 whitespace-nowrap">
                                                {new Date(payment.payment_date).toLocaleDateString('tr-TR')}
                                                {payment.payment_day ? ` - ${payment.payment_day}` : ''}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end space-x-2">
                                            {payment.status === 'pending' && (
                                                <button
                                                    onClick={() => handleMarkAsPaid(payment.id)}
                                                    className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg"
                                                    title="Ödendi Olarak İşaretle"
                                                >
                                                    <Check className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleEditClick(payment)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg"
                                                title="Düzenle"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(payment.id)}
                                                className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                                                title="Sil"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="p-8 text-center text-gray-500">
                        <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Henüz ödeme kaydı bulunmuyor.</p>
                    </div>
                )}
            </div>

            {/* Add / Edit Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-200 dark:border-gray-800">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {editingPayment ? 'Ödeme Kaydını Düzenle' : 'Yeni Ödeme Kaydı'}
                            </h3>
                            <button onClick={handleCloseModal} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
                                ✕
                            </button>
                        </div>
                        <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hoca Adı</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.teacherName}
                                    onChange={(e) => setFormData({ ...formData, teacherName: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                    placeholder="Örn: Ahmet Yılmaz"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ders Tipi</label>
                                    <select
                                        value={formData.lessonType}
                                        onChange={(e) => setFormData({ ...formData, lessonType: e.target.value as any })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                    >
                                        <option value="private">Özel Ders</option>
                                        <option value="course">Kurs</option>
                                        <option value="exam">Deneme Sınavı</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ders Konusu</label>
                                    <input
                                        type="text"
                                        value={formData.lessonSubject}
                                        onChange={(e) => setFormData({ ...formData, lessonSubject: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                        placeholder="Örn: SAT Matematik"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ödeme Adı (Örn: SAT Kitap, Ahmet Yılmaz)</label>
                                <input
                                    type="text"
                                    value={formData.paymentName}
                                    onChange={(e) => setFormData({ ...formData, paymentName: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Saat</label>
                                    <input
                                        type="number"
                                        required
                                        min="0.5"
                                        step="0.5"
                                        value={formData.hoursWorked}
                                        onChange={(e) => setFormData({ ...formData, hoursWorked: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Saatlik Ücret</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.hourlyRate}
                                        onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Para Birimi</label>
                                    <select
                                        value={formData.currency}
                                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                    >
                                        <option value="EUR">EUR (€)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="TRY">TRY (₺)</option>
                                    </select>
                                </div>
                            </div>

                            {formData.hoursWorked && formData.hourlyRate && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                        Toplam: <span className="font-bold">{getCurrencySymbol(formData.currency)}{(Number(formData.hoursWorked) * Number(formData.hourlyRate)).toLocaleString()}</span>
                                    </p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ödeme Durumu</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                    >
                                        <option value="pending">Bekliyor</option>
                                        <option value="paid">Ödendi</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ödeme Kaynağı</label>
                                    <select
                                        disabled={formData.status !== 'paid'}
                                        value={formData.paymentSource}
                                        onChange={(e) => setFormData({ ...formData, paymentSource: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 disabled:opacity-50"
                                    >
                                        <option value="">Seçiniz...</option>
                                        <optgroup label="Nakit">
                                            <option value="Nakit - Bireysel hesap">Bireysel hesap</option>
                                            <option value="Nakit - Skyland ofis">Skyland ofis</option>
                                            <option value="Nakit - Ankara ofis">Ankara ofis</option>
                                            <option value="Nakit - Bursa Ofis">Bursa Ofis</option>
                                            <option value="Nakit - İzmir Ofis">İzmir Ofis</option>
                                            <option value="Nakit - Kadıköy Ofis">Kadıköy Ofis</option>
                                            <option value="Nakit - Fethiye Ofis">Fethiye Ofis</option>
                                        </optgroup>
                                        <optgroup label="Kredi Kartı">
                                            <option value="Kredi Kartı - Pisa Edu Moka">Pisa Edu Moka</option>
                                            <option value="Kredi Kartı - Pisa Test Prep Moka">Pisa Test Prep Moka</option>
                                        </optgroup>
                                        <optgroup label="Şirket">
                                            <option value="Şirket - Pisa Edu">Pisa Edu</option>
                                            <option value="Şirket - Pisa Test Prep">Pisa Test Prep</option>
                                        </optgroup>
                                    </select>
                                </div>
                            </div>

                            {formData.status === 'paid' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ödeme Tarihi</label>
                                        <input
                                            type="date"
                                            value={formData.paymentDate}
                                            onChange={(e) => handleDateChange(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ödeme Günü</label>
                                        <select
                                            value={formData.paymentDay}
                                            onChange={(e) => setFormData({ ...formData, paymentDay: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                        >
                                            <option value="">Seçiniz...</option>
                                            <option value="Pazartesi">Pazartesi</option>
                                            <option value="Salı">Salı</option>
                                            <option value="Çarşamba">Çarşamba</option>
                                            <option value="Perşembe">Perşembe</option>
                                            <option value="Cuma">Cuma</option>
                                            <option value="Cumartesi">Cumartesi</option>
                                            <option value="Pazar">Pazar</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notlar</label>
                                <textarea
                                    rows={2}
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={!formData.teacherName || !formData.hoursWorked || !formData.hourlyRate}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                                >
                                    Kaydet
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeacherPayments;
